import { CheckboxValue } from './../../../CustomizationForm/CustomizationFormCheckbox/index';
import * as analytics from '@anm/analytic';
import { Landing, LandingCTAProps, LandingParameters, UpdateLandingProps, Webinar } from '@anm/api/modules/hosting';
import { FormValues } from '@anm/components/video-landings/TabUrlParameters';
import isUrlAbsolute from '@anm/helpers/is/isUrlAbsolute';
import { toObject, toUrlParams } from '@anm/helpers/parseParams';
import omitBy from 'lodash/fp/omitBy';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import vtConfig from '../../../../../config';
import { brandActions, brandSelectors } from '../../../../../store/modules/brands';
import { userSelectors } from '../../../../../store/modules/user';
import { landingsActions, landingsSelectors } from '../../../../../store/modules/landings';
import { userPrefsSelectors } from '../../../../../store/modules/userprefs';

import { canUpdateParams } from './helpers';

type DefaultButtonProps = {
  landing: Landing;
  isCTADisabled: boolean;
  updateLandingParams(params: UpdateLandingProps): void;
};

type UseShareLinkProps = {
  slug: string;
  hostId: string;
  isWebinar?: boolean;
  initialState?: string;
};

const config = vtConfig();

export const useFetchBranding = () => {
  const dispatch = useDispatch();

  const user = useSelector(userSelectors.selectUserProfile);
  const brands = useSelector(brandSelectors.selectBrands);

  useEffect(() => {
    if (brands.length || !user) return;

    dispatch(brandActions.fetchBrands());
  }, [brands.length, user?.userId]);
};

export const usePrefferedBrandId = () => {
  const dispatch = useDispatch();
  const brands = useSelector(brandSelectors.selectBrands);
  const prefferedBrandId = useSelector(userPrefsSelectors.selectPreferredBrandId);

  useEffect(() => {
    if (!brands || !prefferedBrandId) return;

    dispatch(brandActions.updateCurrentBrandId(+prefferedBrandId));
  }, [brands, prefferedBrandId]);
};

export const useLandingTabActions = (landing?: Landing, trackOpen = true) => {
  const { parameters: landingParams } = landing || {};

  const dispatch = useDispatch();

  useEffect(() => {
    trackOpen && analytics.trackOpenLandingSettings(location.href);
  }, [trackOpen]);

  const updateLandingParams = ({ key, params }: UpdateLandingProps) => {
    dispatch(
      landingsActions.updateLanding({
        parameters: { ...landingParams, [key]: params }
      })
    );
  };

  return [updateLandingParams] as const;
};

export const useShareLink = ({ slug, hostId, isWebinar, initialState }: UseShareLinkProps) => {
  const getLink = (slug?: string) =>
    isWebinar ? `${config.urls.webinarUrl}${hostId}` : `${config.urls.videoLanding}${slug}`;

  const [shareLink, setShareLink] = useState<string>(initialState || getLink(slug));

  useEffect(() => {
    const isSlugEmpty = slug.length === 0;
    const slugEnding = isSlugEmpty ? hostId : `${slug}-${hostId}`;

    setShareLink(getLink(slugEnding));
  }, [slug, isWebinar]);

  return [shareLink, setShareLink] as const;
};

export const useDefaultButtonText = ({ landing, isCTADisabled, updateLandingParams }: DefaultButtonProps) => {
  useEffect(() => {
    const params = landing.parameters?.cta;
    const DEFAULT_BUTTON_TEXT = 'Sign up';

    if (isCTADisabled || params?.buttonText) return;
    updateLandingParams({
      key: 'cta',
      params: {
        ...params,
        disabled: isCTADisabled,
        buttonText: DEFAULT_BUTTON_TEXT
      }
    });
  }, [isCTADisabled]);
};

export const useLandingTabInitialValues = (parameters?: LandingParameters) => {
  const metaFormValues = {
    heading: parameters?.meta?.heading,
    bodyText: parameters?.meta?.bodyText
  };

  const ctaButtonLink = parameters?.cta?.buttonLink;
  const strParams = ctaButtonLink?.split('?')[1] || '';
  const ctaLinkParams = toObject(strParams) as FormValues;

  const CTAFormValues = {
    buttonText: parameters?.cta?.buttonText,
    buttonLink: ctaButtonLink,
    ...ctaLinkParams
  };

  const trackingFormValues = {
    googleTrackId: parameters?.track?.googleTrackId,
    facebookPixelTrackId: parameters?.track?.facebookPixelTrackId
  };

  return [CTAFormValues, metaFormValues, trackingFormValues] as const;
};

export const useCallToActionForm = () => {
  const dispatch = useDispatch();
  const landing = useSelector(landingsSelectors.selectLanding);

  const ctaButtonLink = landing?.parameters?.cta?.buttonLink;

  const onCTAFormChange = useCallback(
    (values: FormValues) => {
      const ctaParams = omitBy((_: string, k: string) => k === 'buttonText' || k === 'buttonLink')(values);

      const newParams = toUrlParams(ctaParams) || '';
      const url = values.buttonLink || '';
      const absoluteLink = isUrlAbsolute(url) || url.startsWith('mailto:') ? url : `http://${url}`;
      const ctaButtonLink = url ? absoluteLink : '';

      const isParamsExists = !!newParams.length;

      const linkWithoutParams = isParamsExists ? ctaButtonLink?.split('?')[0] : ctaButtonLink;

      const divider = isParamsExists ? '?' : '';

      const buttonLink = linkWithoutParams ? `${linkWithoutParams}${divider}${newParams}` : '';

      const isCTADisabled = !(values?.buttonText?.length || buttonLink?.length);

      const currentParams: LandingCTAProps = {
        buttonLink,
        buttonText: values?.buttonText || ''
      };

      const canUpdate = canUpdateParams('cta', currentParams, landing?.parameters);

      canUpdate &&
        dispatch(
          landingsActions.updateLanding({
            ...landing,
            parameters: {
              ...landing?.parameters,
              cta: {
                ...landing?.parameters?.cta,
                ...currentParams,
                disabled: isCTADisabled
              }
            }
          })
        );
    },
    [landing, ctaButtonLink]
  );

  return [onCTAFormChange] as const;
};

export const useToggleChat = (webinar: Webinar) => {
  const dispatch = useDispatch();

  const { chat: isChat } = webinar.parameters;

  const onToggleChat = useCallback(
    ({ value }: CheckboxValue) => {
      dispatch(
        landingsActions.updateLanding({
          ...webinar,
          parameters: {
            ...webinar.parameters,
            chat: value
          }
        })
      );
    },
    [webinar]
  );

  return [isChat, onToggleChat] as const;
};
